import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AutoComplete } from "primereact/autocomplete";
import { FloatLabel } from "primereact/floatlabel";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { EMPTY_VALUE } from "../../constants";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";

function toExcludeSet(excludeUserIds, value, multiple) {
    const ids = new Set();
    if (excludeUserIds) {
        const list = excludeUserIds instanceof Set ? [...excludeUserIds] : excludeUserIds;
        list.forEach((id) => ids.add(Number(id)));
    }
    if (multiple && Array.isArray(value)) {
        value.forEach((user) => {
            if (user && typeof user === "object" && user.id != null) ids.add(Number(user.id));
        });
    }
    return ids;
}

function formatUserLabel(user) {
    if (user == null || user === "") return "";
    if (typeof user === "string") return user;
    const name = user.full_name || user.email || EMPTY_VALUE;
    return user.id != null ? `${name} (#${user.id})` : name;
}

function defaultItemTemplate(user) {
    if (typeof user === "string") {
        return <span className={TEXT_NORMAL}>{user}</span>;
    }
    return (
        <div className="flex flex-column gap-1">
            <span className={`${TEXT_NORMAL} font-semibold`}>{formatUserLabel(user)}</span>
            <span className={`${TEXT_SMALL} text-color-secondary`}>
                {user?.email || EMPTY_VALUE} · {user?.phone || EMPTY_VALUE}
            </span>
        </div>
    );
}

function isUserObject(value) {
    return value && typeof value === "object" && !Array.isArray(value) && value.id != null;
}

export default function UserSearch({
    value,
    onChange,
    multiple = false,
    disabled = false,
    placeholder = "Search by name, email or phone",
    className = "w-full",
    inputId,
    limit = 10,
    minQueryLength = 1,
    excludeUserIds,
    floatLabel = false,
    label = "User",
    showClear = false,
    dropdown = true,
    forceSelection = true,
    itemTemplate,
    selectedItemTemplate,
    requestGetQuery,
    pt,
    ...rest
}) {
    const { requestAPI } = useAppContext();
    const [suggestions, setSuggestions] = useState([]);
    const [, setLoading] = useState(false);
    // Local value so typing (string) is not wiped by object-only parents
    const [innerValue, setInnerValue] = useState(multiple ? value || [] : value ?? null);
    const requestSeq = useRef(0);

    useEffect(() => {
        setInnerValue((prev) => {
            // Keep in-progress typed query; only sync real selections from parent
            if (!multiple && typeof prev === "string") {
                return prev;
            }
            if (multiple && Array.isArray(prev) && prev.some((item) => typeof item === "string")) {
                return prev;
            }
            return multiple ? value || [] : value ?? null;
        });
    }, [value, multiple]);

    const excludeSet = useMemo(() => toExcludeSet(excludeUserIds, innerValue, multiple), [excludeUserIds, innerValue, multiple]);

    const completeMethod = useCallback(
        (event) => {
            const query = (event?.query || "").trim();
            if (query.length < minQueryLength) {
                setSuggestions([]);
                return;
            }

            const seq = ++requestSeq.current;
            requestAPI({
                requestPath: "users",
                requestMethod: "GET",
                requestGetQuery: {
                    search: query,
                    limit,
                    offSet: 0,
                    ...(requestGetQuery || {}),
                },
                setLoading,
                onResponseReceieved: (users, responseCode) => {
                    if (seq !== requestSeq.current) return;
                    if (responseCode === 200) {
                        const dataSet = users?.dataSet || [];
                        setSuggestions(dataSet.filter((user) => !excludeSet.has(Number(user.id))));
                    } else {
                        setSuggestions([]);
                    }
                },
                onRequestFailure: () => {
                    if (seq === requestSeq.current) setSuggestions([]);
                },
            });
        },
        [excludeSet, limit, minQueryLength, requestAPI, requestGetQuery],
    );

    const emitChange = useCallback(
        (next) => {
            if (multiple) {
                const users = Array.isArray(next) ? next.filter(isUserObject) : [];
                onChange?.(users);
                return;
            }
            if (next == null || next === "") {
                onChange?.(null);
                return;
            }
            if (isUserObject(next)) {
                onChange?.(next);
            }
            // string while typing: keep local only, do not notify parent
        },
        [multiple, onChange],
    );

    const handleChange = useCallback(
        (e) => {
            const next = e.value ?? (multiple ? [] : null);
            setInnerValue(next);
            emitChange(next);
        },
        [emitChange, multiple],
    );

    // PrimeReact's forceSelection matches input text against `field`, which never equals the
    // selectedItemTemplate label ("Name (#id)") and clears a valid selection on blur.
    const { onBlur: onBlurProp, appendTo, scrollHeight, ...autoCompleteRest } = rest;
    const handleBlur = useCallback(
        (e) => {
            if (forceSelection && !multiple && typeof innerValue === "string") {
                setInnerValue(isUserObject(value) ? value : null);
            }
            onBlurProp?.(e);
        },
        [forceSelection, innerValue, multiple, onBlurProp, value],
    );

    const autoComplete = (
        <AutoComplete
            inputId={inputId}
            value={innerValue}
            suggestions={suggestions}
            completeMethod={completeMethod}
            field="full_name"
            multiple={multiple}
            dropdown={dropdown}
            forceSelection={false}
            disabled={disabled}
            placeholder={placeholder}
            className={className.includes("mt-") ? className.replace(/\bmt-\d+\b/g, "").trim() || "w-full" : className}
            delay={300}
            appendTo={appendTo ?? (typeof document !== "undefined" ? document.body : undefined)}
            scrollHeight={scrollHeight || "220px"}
            itemTemplate={itemTemplate || defaultItemTemplate}
            selectedItemTemplate={selectedItemTemplate || ((user) => formatUserLabel(user))}
            onChange={handleChange}
            onBlur={handleBlur}
            pt={{
                root: { className: "w-full" },
                input: { className: `${TEXT_NORMAL} w-full` },
                panel: { className: "overflow-y-auto", style: { zIndex: 2000 } },
                ...(pt || {}),
            }}
            {...autoCompleteRest}
        />
    );

    // Keep wrapper DOM stable when showClear is enabled so typing the first character
    // does not remount AutoComplete (which steals focus and clears the query on blur).
    const content = showClear && !multiple ? (
        <div className="flex align-items-center gap-2 w-full">
            <div className="flex-1">{autoComplete}</div>
            <i
                className={`pi pi-times cursor-pointer ${innerValue ? "" : "invisible pointer-events-none"} ${
                    disabled ? "pointer-events-none opacity-50" : ""
                }`}
                title="Clear"
                onClick={() => {
                    if (disabled || !innerValue) return;
                    setInnerValue(null);
                    onChange?.(null);
                }}
            />
        </div>
    ) : (
        autoComplete
    );

    const wrapperClass = className || "w-full";

    if (floatLabel) {
        return (
            <FloatLabel className={wrapperClass}>
                {content}
                <label htmlFor={inputId}>{label}</label>
            </FloatLabel>
        );
    }

    return <div className={wrapperClass}>{content}</div>;
}
